/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { Avatar } from '@/shared/ui/Avatar'
import { SlideOver } from '@/shared/ui/SlideOver'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { Plus, Save, ShieldCheck, Check, X, UserPlus } from 'lucide-react'
import { toast } from '@/store/useToastStore'
import { restClient, getApiError } from '@/api/client'
import { schoolService } from '@/api/services/schoolService'
import { userService } from '@/api/services/userService'
import { useAuthStore } from '@/store/useAuthStore'
import { endpoints } from '@/api/endpoints'
import {
  useSchoolAdminRolesV2, useCreateSchoolAdminRoleV2, useSchoolAdminPermissionsV2, useSchoolAdminRolePermissionsV2,
  useMapSchoolAdminPermissionsV2, useSchoolAdminUserRoleMappingV2, useMapSchoolAdminRoleUsersV2,
} from '@/api/hooks'
import type { SchoolPermissionGrant } from '@/api/services/roleV2Service'
import { toArray, sameSet, type Level } from './rolesUtils'
import { SearchBox, ErrorNote, Center, Box, LevelPills, LEVEL_LABEL } from './rolesUi'

const ACTIONS = ['create', 'read', 'update', 'delete'] as const
type Action = typeof ACTIONS[number]
type Flags = Record<Action, boolean>
const NONE: Flags = { create: false, read: false, update: false, delete: false }
const hasAny = (f?: Flags) => !!f && ACTIONS.some(a => f[a])
const sameFlags = (a?: Flags, b?: Flags) => ACTIONS.every(k => !!a?.[k] === !!b?.[k])

// One simple choice per permission instead of four boxes:
//   View = read · Edit = read + add + change · Full = everything, including delete.
const flagsForLevel = (l: Level): Flags =>
  l === 'none' ? NONE
  : l === 'view' ? { ...NONE, read: true }
  : l === 'edit' ? { create: true, read: true, update: true, delete: false }
  : { create: true, read: true, update: true, delete: true }
const levelOfFlags = (f?: Flags): Level | 'custom' => {
  if (!hasAny(f)) return 'none'
  for (const l of ['view', 'edit', 'full'] as Level[]) if (sameFlags(f, flagsForLevel(l))) return l
  return 'custom'
}

/** "request_for_verification_by_school_admin" → "Request for verification". */
const friendlyName = (raw: string) => {
  const t = raw.replace(/[_-]?(by_)?(school_admin|for_school|by_admin)$/i, '').replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : raw
}

// Starting points for the "New role" form.
const ROLE_IDEAS = [
  { name: 'Front desk', hint: 'Sees bookings and trainers, can’t change much' },
  { name: 'Bookings manager', hint: 'Handles bookings and sessions' },
  { name: 'Accountant', hint: 'Payouts, commission and reports' },
  { name: 'View only', hint: 'Can look but not change anything' },
]

/**
 * School-admin access, on the access-control-for-school-admin API:
 *   1. pick (or create) a role and tick Create / Read / Update / Delete per permission;
 *   2. (platform admins only, `withUsers`) give a school admin one or more of those roles.
 * The school dashboard renders it with `withUsers={false}`.
 */
export function SchoolAdminAccessTab({ withUsers = true }: { withUsers?: boolean }) {
  const confirm = useConfirm()

  // ── roles ───────────────────────────────────────────────────────────────
  const rolesQ = useSchoolAdminRolesV2()
  const roles = useMemo(() => toArray(rolesQ.data, 'roles') as { id: number; name: string; description?: string }[], [rolesQ.data])
  const [selRole, setSelRole] = useState<number | null>(null)
  useEffect(() => {
    if (!roles.length) return
    if (selRole == null || !roles.some(r => r.id === selRole)) setSelRole(roles[0].id)
  }, [roles, selRole])

  // ── permission grid ─────────────────────────────────────────────────────
  const permsQ = useSchoolAdminPermissionsV2()
  const perms = useMemo(() => toArray(permsQ.data, 'permissions') as { id: number; name: string; description?: string }[], [permsQ.data])
  const mapQ = useSchoolAdminRolePermissionsV2(selRole)
  const mapPerms = useMapSchoolAdminPermissionsV2()

  const saved = useMemo(() => {
    const m = new Map<number, Flags>()
    for (const r of toArray(mapQ.data, 'permissions', 'mappings')) {
      const id = r.permission_id ?? r.permission?.id
      if (id == null) continue
      m.set(id, { create: !!r.create, read: !!r.read, update: !!r.update, delete: !!r.delete })
    }
    return m
  }, [mapQ.data])

  const [draft, setDraft] = useState<Map<number, Flags>>(new Map())
  useEffect(() => { setDraft(new Map(saved)) }, [saved, selRole])

  const [permSearch, setPermSearch] = useState('')
  const [advanced, setAdvanced] = useState(false)   // show the four Create/Read/Update/Delete boxes
  const [onlyOn, setOnlyOn] = useState(false)       // hide permissions this role doesn't have
  const shownPerms = perms.filter(p =>
    `${p.name} ${friendlyName(p.name)} ${p.description ?? ''}`.toLowerCase().includes(permSearch.trim().toLowerCase())
    && (!onlyOn || hasAny(draft.get(p.id))))

  const setLevel = (pid: number, l: Level) => setDraft(d => { const n = new Map(d); n.set(pid, flagsForLevel(l)); return n })
  const setLevelForShown = (l: Level) => setDraft(d => { const n = new Map(d); shownPerms.forEach(p => n.set(p.id, flagsForLevel(l))); return n })

  // "Can view 5 · edit 3 · full 2 of 12 areas"
  const summary = useMemo(() => {
    const c = { view: 0, edit: 0, full: 0, custom: 0 }
    perms.forEach(p => { const l = levelOfFlags(draft.get(p.id)); if (l !== 'none') c[l]++ })
    return { ...c, total: perms.length, any: c.view + c.edit + c.full + c.custom }
  }, [perms, draft])

  const dirty = useMemo(() => perms.some(p => !sameFlags(draft.get(p.id), saved.get(p.id))), [perms, draft, saved])

  const setFlag = (pid: number, a: Action, on: boolean) => setDraft(d => {
    const n = new Map(d); n.set(pid, { ...(d.get(pid) ?? NONE), [a]: on }); return n
  })
  // Column header: tick/untick one action for every permission currently shown.
  const columnState = (a: Action): 'none' | 'some' | 'all' => {
    const on = shownPerms.filter(p => draft.get(p.id)?.[a]).length
    return on === 0 ? 'none' : on === shownPerms.length ? 'all' : 'some'
  }
  const toggleColumn = (a: Action) => {
    const turnOn = columnState(a) !== 'all'
    setDraft(d => { const n = new Map(d); shownPerms.forEach(p => n.set(p.id, { ...(d.get(p.id) ?? NONE), [a]: turnOn })); return n })
  }

  const savePerms = async () => {
    if (selRole == null || !dirty) return
    const role = roles.find(r => r.id === selRole)
    if (!(await confirm({
      title: 'Save permission changes?',
      description: `School admins with "${role?.name}" are affected immediately.`,
      confirmLabel: 'Save changes', variant: 'primary',
    }))) return
    // Send every row that has access now, plus rows that had access and lost it (all-false) so they're revoked.
    const rolePermissioMapping: SchoolPermissionGrant[] = perms
      .filter(p => hasAny(draft.get(p.id)) || hasAny(saved.get(p.id)))
      .map(p => ({ role_id: selRole, permission_id: p.id, ...NONE, ...draft.get(p.id) }))
    try { await mapPerms.mutateAsync({ rolePermissioMapping }); toast.success('Permissions saved') }
    catch (e) { toast.error(getApiError(e)) }
  }

  // ── create role ─────────────────────────────────────────────────────────
  const createRole = useCreateSchoolAdminRoleV2()
  const [formOpen, setFormOpen] = useState(false)
  const [name, setName] = useState('')
  const [desc, setDesc] = useState('')
  const saveRole = async () => {
    if (!name.trim()) return
    try {
      const res: any = await createRole.mutateAsync({ name: name.trim(), description: desc })
      const id = res?.data?.id ?? res?.id
      if (id) setSelRole(id)
      toast.success('Role created'); setFormOpen(false); setName(''); setDesc('')
    } catch (e) { toast.error(getApiError(e)) }
  }

  const pickRole = async (id: number) => {
    if (id === selRole) return
    if (dirty && !(await confirm({
      title: 'Discard unsaved changes?', description: 'Switching roles will lose the unsaved permission changes.',
      confirmLabel: 'Discard', variant: 'danger',
    }))) return
    setSelRole(id)
  }
  const current = roles.find(r => r.id === selRole)

  return (
    <div className="space-y-5">
      {/* how it works */}
      <div className="grid sm:grid-cols-3 gap-3">
        {[
          { n: 1, t: 'Create a role', d: 'A role is a job title, like “Front desk” or “Accountant”.' },
          { n: 2, t: 'Choose what it can do', d: 'For each area pick None, View, Edit or Full.' },
          { n: 3, t: 'Give it to people', d: 'Add a person and tick the roles they should have.' },
        ].map(s => (
          <div key={s.n} className="flex items-start gap-3 rounded-xl bg-white border border-gray-100 px-4 py-3">
            <span className="w-6 h-6 shrink-0 rounded-full bg-brand-50 text-brand-600 text-xs font-bold flex items-center justify-center">{s.n}</span>
            <span><span className="block text-sm font-semibold text-gray-800">{s.t}</span><span className="block text-xs text-gray-500">{s.d}</span></span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 items-start">
        {/* ── Roles ── */}
        <Card className="lg:sticky lg:top-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900">Roles <span className="text-gray-400 font-normal text-sm">({roles.length})</span></h3>
            <Button size="sm" icon={<Plus size={13} />} onClick={() => setFormOpen(true)}>New</Button>
          </div>
          {rolesQ.isLoading ? <Center /> : rolesQ.isError ? <ErrorNote err={rolesQ.error} onRetry={() => rolesQ.refetch()} /> :
            roles.length === 0 ? (
              <div className="text-center py-8 space-y-3">
                <ShieldCheck className="mx-auto text-gray-300" size={32} />
                <p className="text-sm text-gray-500">No roles yet. Start with step 1 — create your first role.</p>
                <Button size="sm" icon={<Plus size={13} />} onClick={() => setFormOpen(true)}>Create the first role</Button>
              </div>
            ) : (
              <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                {roles.map(r => (
                  <button key={r.id} onClick={() => pickRole(r.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${r.id === selRole ? 'bg-brand-50 border-brand-200' : 'border-transparent hover:bg-gray-50'}`}>
                    <span className={`block text-sm truncate ${r.id === selRole ? 'font-semibold text-brand-700' : 'font-medium text-gray-800'}`}>{r.name}</span>
                    {r.description && <span className="block text-xs text-gray-400 truncate mt-0.5">{r.description}</span>}
                  </button>
                ))}
              </div>
            )}
        </Card>

        {/* ── Permission grid ── */}
        <Card className="min-w-0">
          {!current ? (
            <p className="text-sm text-gray-400 py-16 text-center">{rolesQ.isLoading ? 'Loading…' : 'Select or create a role to manage its permissions.'}</p>
          ) : (
            <>
              <div className="mb-4">
                <h3 className="font-bold text-gray-900 text-lg truncate">{current.name}</h3>
                <p className="text-sm text-gray-500">{current.description || 'No description'}</p>
                {!permsQ.isLoading && !mapQ.isLoading && (
                  <p className={`text-xs mt-1.5 ${summary.any ? 'text-gray-600' : 'text-amber-600'}`}>
                    {summary.any
                      ? `Can access ${summary.any} of ${summary.total} areas${[
                          summary.view && ` · ${summary.view} view`, summary.edit && ` · ${summary.edit} edit`,
                          summary.full && ` · ${summary.full} full`, summary.custom && ` · ${summary.custom} custom`,
                        ].filter(Boolean).join('')}`
                      : 'This role cannot do anything yet — choose what it can do below.'}
                  </p>
                )}
              </div>

              {permsQ.isLoading || mapQ.isLoading ? <Center /> :
                permsQ.isError ? <ErrorNote err={permsQ.error} onRetry={() => permsQ.refetch()} /> :
                mapQ.isError ? <ErrorNote err={mapQ.error} onRetry={() => mapQ.refetch()} /> : (
                  <>
                    {/* quick presets + search */}
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <SearchBox value={permSearch} onChange={setPermSearch} placeholder="Search, e.g. bookings, trainers, reports…" className="flex-1 min-w-[220px] max-w-sm" />
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" className="accent-brand-500" checked={onlyOn} onChange={e => setOnlyOn(e.target.checked)} />
                        Only what this role can do
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none sm:ml-auto">
                        <input type="checkbox" className="accent-brand-500" checked={advanced} onChange={e => setAdvanced(e.target.checked)} />
                        Advanced (add / change / delete separately)
                      </label>
                    </div>
                    {!advanced && shownPerms.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-xl bg-gray-50">
                        <span className="text-sm text-gray-600">Set {permSearch || onlyOn ? 'all shown' : 'everything'} to:</span>
                        <LevelPills small onPick={setLevelForShown} />
                        <span className="text-xs text-gray-400 w-full sm:w-auto">View = look only · Edit = look, add &amp; change · Full = also delete</span>
                      </div>
                    )}

                    <div className="border border-gray-100 rounded-xl overflow-hidden">
                      {advanced && (
                        <div className="grid grid-cols-[1fr_repeat(4,64px)] items-center gap-2 px-4 py-2 bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                          <span>What it covers</span>
                          {ACTIONS.map(a => (
                            <span key={a} className="flex flex-col items-center gap-1">
                              {a === 'read' ? 'view' : a === 'update' ? 'change' : a === 'create' ? 'add' : a}
                              <Box small state={columnState(a)} onClick={shownPerms.length ? () => toggleColumn(a) : undefined} />
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="divide-y divide-gray-50 max-h-[55vh] overflow-y-auto">
                        {shownPerms.length === 0 && (
                          <p className="text-sm text-gray-400 text-center py-8">
                            {!perms.length ? 'No permissions are available yet.' : onlyOn && !permSearch ? 'This role cannot do anything yet — untick “Only what this role can do” to start.' : `Nothing matches “${permSearch}”.`}
                          </p>
                        )}
                        {shownPerms.map(p => {
                          const lvl = levelOfFlags(draft.get(p.id))
                          return (
                            <div key={p.id} className={`items-center gap-3 px-4 py-2.5 hover:bg-gray-50/60 ${advanced ? 'grid grid-cols-[1fr_repeat(4,64px)]' : 'flex flex-wrap justify-between'}`}>
                              <span className="min-w-0">
                                <span className="block text-sm font-medium text-gray-800 truncate" title={p.name}>{friendlyName(p.name)}</span>
                                {p.description && <span className="block text-xs text-gray-400 truncate">{p.description}</span>}
                              </span>
                              {advanced ? ACTIONS.map(a => (
                                <span key={a} className="flex justify-center">
                                  <Box state={draft.get(p.id)?.[a] ? 'all' : 'none'} onClick={() => setFlag(p.id, a, !draft.get(p.id)?.[a])} />
                                </span>
                              )) : (
                                <span className="flex items-center gap-2">
                                  {lvl === 'custom' && <span className="text-[11px] text-amber-600 font-medium">{LEVEL_LABEL.custom}</span>}
                                  <LevelPills small active={lvl} onPick={l => setLevel(p.id, l)} />
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </>
                )}

              {dirty && (
                <div className="sticky bottom-3 mt-4 flex items-center justify-between gap-3 rounded-xl bg-gray-900 text-white px-4 py-3 shadow-lg z-10">
                  <span className="text-sm">You have unsaved permission changes</span>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" className="!text-gray-200 hover:!bg-white/10" onClick={() => setDraft(new Map(saved))}>Discard</Button>
                    <Button size="sm" icon={<Save size={13} />} loading={mapPerms.isPending} onClick={savePerms}>Save changes</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {withUsers && <SchoolAdminUsersCard roles={roles} />}

      <SlideOver open={formOpen} onClose={() => setFormOpen(false)} title="New role">
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Quick ideas</p>
            <div className="flex flex-wrap gap-2">
              {ROLE_IDEAS.map(i => (
                <button key={i.name} type="button" title={i.hint} onClick={() => { setName(i.name); setDesc(i.hint) }}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 hover:border-brand-300 hover:bg-brand-50">{i.name}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Role name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Bookings manager" autoFocus
              onKeyDown={e => e.key === 'Enter' && saveRole()}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">Description</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={3} placeholder="What is this role for?"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </div>
          <p className="text-xs text-gray-400">Next you’ll choose what this role can do.</p>
          <Button className="w-full justify-center" disabled={!name.trim()} loading={createRole.isPending} onClick={saveRole}>Create role</Button>
        </div>
      </SlideOver>
    </div>
  )
}

/** Pick a school admin, tick the roles they should have. */
function SchoolAdminUsersCard({ roles }: { roles: { id: number; name: string; description?: string }[] }) {
  const confirm = useConfirm()
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => { const t = setTimeout(() => setDebounced(term.trim()), 300); return () => clearTimeout(t) }, [term])

  // A school admin gives roles to their school's trainers; a platform admin to school admins.
  const isSchoolAdmin = useAuthStore(s => s.user?.user_type === 'school_admin')
  const searchQ = useQuery({
    queryKey: ['role-assign-users', isSchoolAdmin, debounced],
    queryFn: () => isSchoolAdmin
      ? schoolService.schoolTrainers({ search: debounced, limit: 8 })
      : restClient.get(`${endpoints.school.getSchoolAdmin}?page=1&limit=8&search=${encodeURIComponent(debounced)}`).then(r => r.data),
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  })
  // Trainer rows may wrap the person in `user`.
  const results: any[] = useMemo(() => toArray(searchQ.data, 'users').map((r: any) => r.user ?? r), [searchQ.data])
  const [user, setUser] = useState<any | null>(null)
  const [newOpen, setNewOpen] = useState(false)

  const userRolesQ = useSchoolAdminUserRoleMappingV2({ user_id: user?.id }, user != null)
  const saved = useMemo(() => new Set(
    toArray(userRolesQ.data, 'roles', 'mappings')
      .map((m: any) => m.role_id ?? m.role?.id ?? m.id)
      .filter((x: unknown): x is number => typeof x === 'number'),
  ), [userRolesQ.data])
  const [draft, setDraft] = useState<Set<number>>(new Set())
  useEffect(() => { setDraft(new Set(saved)) }, [saved])
  const dirty = !sameSet(draft, saved)

  const mapUsers = useMapSchoolAdminRoleUsersV2()
  const toggle = (id: number) => setDraft(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const pick = async (u: any | null) => {
    if (dirty && !(await confirm({ title: 'Discard unsaved changes?', description: `Role changes for ${user?.name ?? 'this user'} were not saved.`, confirmLabel: 'Discard', variant: 'danger' }))) return
    setUser(u); setTerm(''); setDebounced('')
  }

  const save = async () => {
    if (!user) return
    if (!(await confirm({
      title: 'Save roles?', description: `${user.name ?? 'This school admin'} will have ${draft.size} role${draft.size === 1 ? '' : 's'}. Their access changes immediately.`,
      confirmLabel: 'Save roles', variant: 'primary',
    }))) return
    try { await mapUsers.mutateAsync({ role_id: [...draft], user_id: [user.id] }); toast.success('School admin roles saved') }
    catch (e) { toast.error(getApiError(e)) }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-bold text-gray-900">{isSchoolAdmin ? 'Assign roles to a user' : 'Assign roles to a school admin'}</h3>
          <p className="text-sm text-gray-500">{isSchoolAdmin ? 'Find the person in your school' : 'Find the school admin'}, then tick the roles they should have.</p>
        </div>
        {isSchoolAdmin && <Button size="sm" icon={<UserPlus size={13} />} onClick={() => setNewOpen(true)}>New user</Button>}
      </div>
      {isSchoolAdmin && (
        <NewSchoolUserForm open={newOpen} onClose={() => setNewOpen(false)} roles={roles}
          onCreated={u => { setUser(u); setTerm(''); setDebounced('') }} />
      )}

      <div className="relative max-w-md mb-5">
        <SearchBox value={term} onChange={setTerm} placeholder="Search by name, email or phone…" />
        {term.trim().length === 1 && <p className="text-xs text-gray-400 mt-1.5">Type at least 2 characters to search…</p>}
        {debounced.length >= 2 && (
          <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
            {searchQ.isLoading ? <div className="py-4"><Center /></div> :
              searchQ.isError ? <div className="p-3"><ErrorNote err={searchQ.error} onRetry={() => searchQ.refetch()} /></div> :
              results.length === 0 ? <p className="text-sm text-gray-400 text-center py-4">No school admins found for “{debounced}”</p> :
                results.map(u => (
                  <button key={u.id} onClick={() => pick(u)} className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50">
                    <Avatar name={u.name ?? 'U'} src={u.photo_url} size="sm" />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-800 truncate">{u.name ?? '—'}</span>
                      <span className="block text-xs text-gray-400 truncate">{u.email ?? u.phone ?? `#${u.id}`}</span>
                    </span>
                  </button>
                ))}
          </div>
        )}
      </div>

      {!user ? (
        <p className="text-sm text-gray-400 py-8 text-center border border-dashed border-gray-200 rounded-xl">Search for a school admin above to manage their roles.</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gray-50 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={user.name ?? 'U'} src={user.photo_url} size="md" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user.name ?? `User #${user.id}`}</p>
                <p className="text-xs text-gray-500 truncate">{user.email ?? user.phone ?? `#${user.id}`}</p>
              </div>
            </div>
            <button className="text-gray-400 hover:text-gray-600" onClick={() => pick(null)} aria-label="Clear user"><X size={16} /></button>
          </div>

          {userRolesQ.isLoading ? <Center /> : userRolesQ.isError ? <ErrorNote err={userRolesQ.error} onRetry={() => userRolesQ.refetch()} /> :
            roles.length === 0 ? <p className="text-sm text-gray-400 py-6 text-center">No school admin roles exist yet — create one above.</p> : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
                  {roles.map(r => {
                    const on = draft.has(r.id)
                    return (
                      <button key={r.id} onClick={() => toggle(r.id)}
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${on ? 'border-brand-300 bg-brand-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <span className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 ${on ? 'bg-brand-500 text-white' : 'bg-gray-200'}`}>{on && <Check size={11} />}</span>
                        <span className="min-w-0">
                          <span className={`block text-sm truncate ${on ? 'font-semibold text-brand-800' : 'font-medium text-gray-800'}`}>{r.name}</span>
                          {r.description && <span className="block text-xs text-gray-400 truncate">{r.description}</span>}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <div className="flex items-center gap-3">
                  <Button icon={<Save size={13} />} disabled={!dirty} loading={mapUsers.isPending} onClick={save}>Save roles</Button>
                  {dirty && <button className="text-sm text-gray-500 hover:text-gray-700" onClick={() => setDraft(new Set(saved))}>Discard changes</button>}
                </div>
              </>
            )}
        </>
      )}
    </Card>
  )
}

const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400'
const labelCls = 'text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5'

/** The logged-in school admin's own school (vendor) id — the vendor arrives in several shapes. */
function useMySchoolId(): number | undefined {
  const user = useAuthStore(s => s.user) as any
  const q = useQuery({ queryKey: ['my-vendor', 'roles-new-user'], queryFn: () => schoolService.myVendor(), staleTime: 300_000 })
  const body: any = (q.data as any)?.data ?? q.data ?? {}
  const row: any = Array.isArray(body) ? (body[0] ?? {}) : Array.isArray(body?.data) ? (body.data[0] ?? {}) : body
  const obj: any = row.school ?? row.vendor ?? row
  const id = row.vendor_id ?? row.vendor?.id ?? obj?.id ?? row.id ?? user?.school_id ?? user?.school?.id
  return id != null && !Number.isNaN(Number(id)) ? Number(id) : undefined
}

/** Create a school staff user (user_type SCHOOL_ADMIN, tied to this school) and give them roles in one step. */
function NewSchoolUserForm({ open, onClose, roles, onCreated }: {
  open: boolean
  onClose: () => void
  roles: { id: number; name: string; description?: string }[]
  onCreated: (u: { id: number; name: string; email?: string }) => void
}) {
  const schoolId = useMySchoolId()
  const mapUsers = useMapSchoolAdminRoleUsersV2()
  const [f, setF] = useState({ name: '', email: '', phone: '', password: '' })
  const [roleIds, setRoleIds] = useState<number[]>([])
  const [busy, setBusy] = useState(false)
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.value }))
  const valid = f.name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email) && f.password.length >= 6 && schoolId != null

  const submit = async () => {
    if (!valid || busy) return
    setBusy(true)
    try {
      const body = new FormData()
      body.append('name', f.name.trim())
      body.append('email', f.email.trim())
      body.append('password', f.password)
      if (f.phone.trim()) { body.append('phone', f.phone.trim()); body.append('country_code', '971') }
      body.append('user_type', 'SCHOOL_ADMIN')
      body.append('vendor_id', String(schoolId))
      body.append('is_active', '1')
      const res: any = await userService.create(body as any)
      const d = res?.data?.data ?? res?.data ?? res
      const id = Number(d?.user_id ?? d?.id ?? d?.user?.id)
      if (!id || Number.isNaN(id)) {
        toast.success('User created. Find them in the search to assign roles.')
      } else {
        if (roleIds.length) {
          try { await mapUsers.mutateAsync({ role_id: roleIds, user_id: [id] }) }
          catch (e) { toast.error(`User created, but roles were not assigned: ${getApiError(e)}`) }
        }
        toast.success('User created')
        onCreated({ id, name: f.name.trim(), email: f.email.trim() })
      }
      setF({ name: '', email: '', phone: '', password: '' }); setRoleIds([]); onClose()
    } catch (e) { toast.error(getApiError(e)) }
    finally { setBusy(false) }
  }

  return (
    <SlideOver open={open} onClose={onClose} title="New school user">
      <div className="space-y-4">
        <div><label className={labelCls}>Name *</label><input className={inputCls} value={f.name} onChange={set('name')} autoFocus /></div>
        <div><label className={labelCls}>Email *</label><input className={inputCls} type="email" value={f.email} onChange={set('email')} /></div>
        <div><label className={labelCls}>Phone</label><input className={inputCls} value={f.phone} onChange={set('phone')} placeholder="5XXXXXXXX" /></div>
        <div>
          <label className={labelCls}>Password * <span className="normal-case font-normal text-gray-400">(min 6 characters)</span></label>
          <input className={inputCls} type="password" value={f.password} onChange={set('password')} autoComplete="new-password" />
        </div>
        <div>
          <label className={labelCls}>Roles</label>
          {roles.length === 0 ? <p className="text-xs text-gray-400">No roles yet — create one first, then assign it.</p> : (
            <div className="flex flex-wrap gap-2">
              {roles.map(r => {
                const on = roleIds.includes(r.id)
                return (
                  <button type="button" key={r.id} title={r.description} onClick={() => setRoleIds(c => on ? c.filter(x => x !== r.id) : [...c, r.id])}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors ${on ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                    {on && <Check size={12} />}{r.name}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        {schoolId == null && <p className="text-xs text-amber-600">Loading your school…</p>}
        <Button className="w-full justify-center" disabled={!valid} loading={busy} onClick={submit}>Create user</Button>
      </div>
    </SlideOver>
  )
}
